import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Grid,
  Stack,
  Button,
  Alert,
  Chip,
  Divider,
  FormHelperText,
  CircularProgress,
} from '@mui/material';
import {
  AccountBalance as AccountBalanceIcon,
  CreditCard as CreditCardIcon,
  Business as BusinessIcon,
  CheckCircle as CheckCircleIcon,
  Info as InfoIcon,
  Smartphone as SmartphoneIcon,
} from '@mui/icons-material';
import axios from 'axios';
import { KeyUri, config } from "../key";

export function PaymentDetails() {
  const [formData, setFormData] = useState({
    accountHolderName: '',
    bankName: '',
    accountNumber: '',
    ifscCode: '',
    accountType: '',
    upiVpa: '',
    upiName: '',
    paymentMethod: 'bank_transfer',
  });

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');

  
  useEffect(() => {
    loadPaymentDetails();
  }, []);

  const loadPaymentDetails = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('vendorToken');
      
      const response = await axios.get(`${KeyUri.BACKEND_URI}/payment-details`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

     
      const { paymentDetails } = response.data;
      setFormData(prev => ({
        ...prev,
        ...paymentDetails,
        paymentMethod: paymentDetails?.upiVpa ? 'upi' : 'bank_transfer',
      }));
    } catch (err) {
      console.error('Failed to load payment details:', err);
      if (err.response?.status === 401) {
        setError('Session expired. Please login again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field) => (event) => {
    setFormData((prev) => ({ ...prev, [field]: event.target.value }));
    setSaved(false);
    setError('');
  };

  const validateForm = () => {
    const requiredFields = ['accountHolderName', 'bankName', 'accountNumber', 'ifscCode'];
    
    for (let field of requiredFields) {
      if (!formData[field]?.trim()) {
        setError(`${field.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())} is required`);
        return false;
      }
    }

    if (!/^[A-Z]{4}0[A-Z0-9]{6}$/.test(formData.ifscCode.toUpperCase())) {
      setError('Invalid IFSC code format (e.g., HDFC0001234)');
      return false;
    }

    if (formData.paymentMethod === 'upi' && !formData.upiVpa?.trim()) {
      setError('UPI ID is required when UPI is selected');
      return false;
    }

    return true;
  };

  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    try {
      setSaving(true);
      setError('');
      
      const token = localStorage.getItem('vendorToken');
      
     
      const response = await axios.put(`${KeyUri.BACKEND_URI}/payment-details`, formData, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      console.error('Save error:', err);
      if (err.response?.status === 401) {
        setError('Session expired. Please login again.');
      } else if (err.response?.data?.message) {
        setError(err.response.data.message);
      } else {
        setError('Failed to save payment details. Please try again.');
      }
    } finally {
      setSaving(false);
    }
  };

  const isUpiSelected = formData.paymentMethod === 'upi';

  if (loading) {
    return (
      <Box sx={{ p: 4, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 4, maxWidth: 800, mx: 'auto' }}>
      {/* Page Header */}
      <Stack direction="row" spacing={1} alignItems="center" mb={4}>
        <AccountBalanceIcon color="primary" />
        <Box>
          <Typography variant="h4" component="h1" gutterBottom>
            Payment Details
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Manage your bank account, UPI and payment preferences
          </Typography>
        </Box>
      </Stack>

      <Box component="form" onSubmit={handleSubmit} sx={{ mb: 4 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {/* Rest of your JSX remains exactly the same */}
        {/* Payment Method Selection */}
        <Card sx={{ mb: 4 }}>
          <CardHeader
            avatar={<CreditCardIcon color="primary" />}
            title={<Typography variant="h6">Payment Method</Typography>}
            subheader="Select your preferred payment method"
          />
          <CardContent>
            <FormControl fullWidth>
              <InputLabel>Payment Method</InputLabel>
              <Select
                value={formData.paymentMethod}
                label="Payment Method"
                onChange={handleInputChange('paymentMethod')}
                disabled={saving}
              >
                <MenuItem value="bank_transfer">
                  <Stack direction="row" spacing={1} alignItems="center">
                    <AccountBalanceIcon fontSize="small" sx={{ mr: 1.5 }} />
                    Bank Transfer
                  </Stack>
                </MenuItem>
                <MenuItem value="upi">
                  <Stack direction="row" spacing={1} alignItems="center">
                    <SmartphoneIcon fontSize="small" sx={{ mr: 1.5 }} />
                    UPI Instant
                  </Stack>
                </MenuItem>
              </Select>
            </FormControl>
          </CardContent>
        </Card>

        {/* Bank Account Details */}
        <Card sx={{ mb: 4 }}>
          <CardHeader
            avatar={<BusinessIcon color="primary" />}
            title={<Typography variant="h6">Bank Account Information</Typography>}
            subheader="Enter your banking details for payouts"
          />
          <CardContent>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Account Holder Name"
                  placeholder="John Doe"
                  value={formData.accountHolderName}
                  onChange={handleInputChange('accountHolderName')}
                  required
                  disabled={saving}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Bank Name"
                  placeholder="HDFC Bank / SBI / ICICI"
                  value={formData.bankName}
                  onChange={handleInputChange('bankName')}
                  required
                  disabled={saving}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Account Number"
                  placeholder="123456789012"
                  value={formData.accountNumber}
                  onChange={handleInputChange('accountNumber')}
                  required
                  disabled={saving}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="IFSC Code"
                  placeholder="HDFC0001234"
                  value={formData.ifscCode}
                  onChange={handleInputChange('ifscCode')}
                  required
                  disabled={saving}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth disabled={saving}>
                  <InputLabel>Account Type</InputLabel>
                  <Select
                    value={formData.accountType}
                    label="Account Type"
                    onChange={handleInputChange('accountType')}
                  >
                    <MenuItem value="savings">Savings</MenuItem>
                    <MenuItem value="current">Current</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* UPI Details Section */}
        {isUpiSelected && (
          <Card sx={{ mb: 4 }}>
            <CardHeader
              avatar={<SmartphoneIcon color="success" />}
              title={<Typography variant="h6">UPI Payment Details</Typography>}
              subheader="Add your UPI Virtual Payment Address for instant payouts"
            />
            <CardContent>
              <Grid container spacing={3}>
                <Grid item xs={12} md={8}>
                  <TextField
                    fullWidth
                    label="UPI ID / VPA"
                    placeholder="yourname@paytm or vendor123@ybl"
                    value={formData.upiVpa}
                    onChange={handleInputChange('upiVpa')}
                    helperText="Format: username@paytm, username@oksbi, username@ybl, username@phonepe etc."
                    disabled={saving}
                    required
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="UPI Name"
                    placeholder="Vendor Name"
                    value={formData.upiName}
                    onChange={handleInputChange('upiName')}
                    disabled={saving}
                  />
                </Grid>
              </Grid>
              <Divider sx={{ my: 2 }} />
              <Stack direction="row" spacing={1} flexWrap="wrap">
                <Chip label="Paytm" variant="outlined" size="small" />
                <Chip label="PhonePe" variant="outlined" size="small" />
                <Chip label="Google Pay" variant="outlined" size="small" />
                <Chip label="BHIM" variant="outlined" size="small" />
                <Chip label="Bank UPI" variant="outlined" size="small" />
              </Stack>
              <FormHelperText>
                UPI payouts are instant and available 24x7. Minimum payout ₹1
              </FormHelperText>
            </CardContent>
          </Card>
        )}

        {/* Security Notice */}
        <Card variant="outlined" sx={{ bgcolor: 'info.light', mb: 4 }}>
          <CardContent>
            <Stack direction="row" spacing={2} alignItems="flex-start">
              <InfoIcon color="info" sx={{ mt: 0.5 }} />
              <Box>
                <Typography variant="body2" color="info.main" gutterBottom>
                  Your payment information is encrypted using AES-256 and stored securely.
                </Typography>
                <Typography variant="caption" color="info.dark">
                  UPI and bank payouts processed via RazorpayX. Never share OTPs or banking details.
                </Typography>
              </Box>
            </Stack>
          </CardContent>
        </Card>

        {/* Action Buttons */}
        <Stack direction="row" justifyContent="flex-end" spacing={2}>
          <Button variant="outlined" type="button" disabled={saving}>
            Cancel
          </Button>
          <Button
            variant="contained"
            type="submit"
            disabled={saving || saved}
            startIcon={saving ? <CircularProgress size={20} /> : saved && <CheckCircleIcon />}
          >
            {saving ? 'Saving...' : saved ? 'Saved!' : 'Save Payment Details'}
          </Button>
        </Stack>

        {saved && (
          <Alert severity="success" sx={{ mt: 2 }} icon={<CheckCircleIcon />}>
            Payment details updated successfully! 
            {isUpiSelected 
              ? 'UPI payouts will be processed instantly.' 
              : 'Bank payouts will be credited within 2 working days.'
            }
          </Alert>
        )}
      </Box>
    </Box>
  );
}
